import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';

/**
 * Duración del deslizamiento de un paso, en ms. Tiene que coincidir con la transición declarada en
 * `sections/product-reels.liquid` — el JS la usa para saber cuándo terminó el paso.
 */
const STEP_MS = 520;

/** Píxeles de movimiento a partir de los cuales un arrastre deja de contar como toque. */
const DRAG_THRESHOLD = 5;

/** Fricción de la inercia por fotograma (a 60fps). Más bajo = frena antes. */
const FRICTION = 0.94;

/** Debajo de esta velocidad (px/fotograma) la inercia se da por terminada y se encaja. */
const MIN_VELOCITY = 0.4;

/**
 * Ancho al que se apunta para cada tarjeta en escritorio, en px. Las columnas salen de dividir el
 * ancho medido por este número, así que el resultado son columnas FRACCIONARIAS a propósito: el
 * pedazo de tarjeta que asoma por el borde es lo que le avisa al cliente que hay más para deslizar.
 * Una cuenta que diera un número entero dejaría la fila cortada al ras y parecería que ahí termina.
 */
const TARGET_CARD_WIDTH = 320;

/** Piso de columnas visibles: por angosta que sea la pantalla, siempre tiene que asomar la próxima. */
const MIN_COLUMNS = 1.18;

/**
 * Fracción del alto de la ventana que puede ocupar una tarjeta. Es el tope que manda: una tarjeta
 * 9:16 al ancho de un teléfono mide MÁS que la pantalla, y en escritorio pedir pocas columnas daría
 * tarjetas de 700px de alto. Del alto máximo se deriva el ancho máximo, nunca al revés.
 */
const MAX_HEIGHT_RATIO_DESKTOP = 0.7;
const MAX_HEIGHT_RATIO_MOBILE = 0.62;

/** Proporción de la tarjeta (ancho / alto), para derivar un ancho a partir de un alto. */
const CARD_ASPECT = 9 / 16;

/** En celular la tarjeta nunca ocupa más de esto ni menos de esto del ancho de la pantalla. */
const MOBILE_MAX_WIDTH_RATIO = 0.8;
const MOBILE_MIN_WIDTH_RATIO = 0.58;

/** Salto de la barra de progreso con el teclado, en segundos. */
const SEEK_STEP = 5;

/**
 * Cambio mínimo de avance que justifica reescribir `--pr-progress`. 0.002 es menos de un píxel en
 * una tarjeta de 320px, y acá puede haber seis barras avanzando a la vez: sin este umbral cada
 * fotograma invalidaría el estilo de las seis para no mover nada.
 */
const PROGRESS_EPSILON = 0.002;

/**
 * @param {number} seconds
 * @returns {string}
 */
function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const mins = Math.floor(total / 60);
  return `${mins}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * Carrusel infinito de reels (video vertical y fotos) de la ficha de producto. Funciona en celular y
 * en escritorio, y **cada tarjeta es el reproductor**: play/pausa tocando en cualquier parte, barra
 * de progreso interactiva en el borde de abajo, sonido y descripción desplegable. No hay visor a
 * pantalla completa — si la tarjeta ya reproduce, abrir una capa encima sólo para tener controles
 * era un paso de más.
 *
 * Por qué un componente propio y no el slideshow del tema (`assets/slideshow.js`): ese componente
 * avanza de a `visibleSlides.length`, su transición de loop sin costura está condicionada a
 * `visibleSlides.length === 1` y su arrastre hace `clamp(0, slides.length - 1)` ignorando el
 * atributo `infinite`, así que nunca da la vuelta. Y parchearlo no es opción: lo comparten el hero,
 * la galería DENTRO de cada tarjeta de producto, las recomendaciones, las listas de colección y el
 * buscador predictivo. El mismo razonamiento está en `assets/product-carousel.js`, de donde sale el
 * motor de loop de acá.
 *
 * Dos diferencias deliberadas con `product-carousel.js`: éste TAMBIÉN corre en celular (una fila de
 * reels es un formato de celular, y ahí está la enorme mayoría de las ventas de esta tienda), y no
 * tiene avance automático — con videos reproduciéndose, moverlos solos le arranca de la mano al
 * cliente justo el que está mirando.
 *
 * Mecanismo del loop: la pista se clona en sets completos hasta cubrir la ventana más un set de
 * sobra, y la posición se mantiene normalizada en `[0, anchoSet)`. Como el contenido en `p` y en
 * `p - anchoSet` es idéntico píxel a píxel, restar un set es invisible — eso es todo el "infinito",
 * y es también lo que hace que el arrastre no tenga tope en ninguna dirección. El único cuidado es
 * que ese salto ocurra con la transición apagada, nunca a mitad de una.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport
 * @property {HTMLElement} track
 *
 * @extends Component<Refs>
 */
class ProductReels extends Component {
  /**
   * `requiredRefs` LANZA dentro de `connectedCallback` antes de que corra una sola línea propia y
   * deja el componente muerto en el DOM, así que acá sólo va lo que existe SIEMPRE: las partes de
   * cada tarjeta (video, barra, hoja) se buscan a mano, porque un producto de puras fotos no tiene
   * ninguna de las tres y igual tiene que funcionar.
   */
  requiredRefs = ['viewport', 'track'];

  /** Posición actual de la pista, en px, normalizada a `[0, #setWidth)` cuando el loop está activo. */
  #position = 0;

  #cardWidth = 0;
  #gap = 0;
  #lead = 0;
  #setWidth = 0;
  #realCount = 0;

  /**
   * Si ya se barajó. `#setup` corre de nuevo en cada resize y en cada morph, y rebarajar ahí le
   * cambiaría el orden a la fila bajo el dedo del cliente: la mezcla es por CARGA de página.
   */
  #shuffled = false;

  /**
   * Desplazamiento fijo de DIBUJO, en px: cuántos sets clonados hay ANTES del primero real. Es lo que
   * llena el asomo de la izquierda. No es estado del carrusel — `#position` sigue viviendo en
   * `[0, setWidth)` y nada de la matemática del loop lo mira; se suma sólo al pintar.
   */
  #baseOffset = 0;

  /** Ancho de ventana de la última medición, para ignorar los resizes sólo verticales. */
  #measuredWidth = 0;

  /** @type {number | undefined} */
  #settleTimer;

  /** @type {number | undefined} */
  #inertiaFrame;

  #active = false;
  #dragging = false;
  #dragMoved = 0;
  #captured = false;

  /** @type {{ x: number, t: number }[]} */
  #samples = [];

  #dragStartX = 0;
  #dragStartPosition = 0;
  #velocity = 0;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {IntersectionObserver | undefined} */
  #sectionObserver;

  /**
   * Tercer observador, para los PÓSTERS diferidos (`snippets/product-reel-card.liquid` los emite
   * con `data-src`/`data-srcset` a partir de la tercera tarjeta, o en todas cuando la fila se
   * baraja). Raíz = la ventana del carrusel, con un margen horizontal de una ventana entera hacia
   * cada lado: la tarjeta siguiente ya tiene su foto antes de asomar. Como toda raíz explícita, NO
   * sabe si la sección está en pantalla: por eso sólo anota candidatas en `#postersNear` y quien
   * promueve es `#flushPosters`, que exige además `#sectionVisible`. Los clones heredan los
   * `data-*` por `cloneNode`, así que también esperan, y al promoverse resuelven el mismo candidato
   * que su original (mismo `sizes`) y salen de la caché.
   * @type {IntersectionObserver | undefined}
   */
  #posterObserver;

  /** Tarjetas con póster diferido que ya están cerca del área visible. @type {Set<Element>} */
  #postersNear = new Set();

  /**
   * Si la sección está a la vista EN LA PANTALLA. Es un observador aparte y no un lujo: el otro usa
   * como raíz la ventana del carrusel, y con una raíz explícita la intersección se calcula contra esa
   * caja aunque la sección entera esté fuera de pantalla. Sin este control, al scrollear más allá de
   * los reels los videos seguían corriendo y el que tuviera el sonido activado dejaba el audio
   * sonando de fondo por toda la ficha de producto.
   *
   * Arranca en FALSE y no en true, y eso no es una precaución: los dos observadores entregan su
   * primera observación en orden de creación, así que con el valor optimista el de las tarjetas
   * corría creyendo que la sección estaba a la vista, promovía los `src` y llamaba `load()`; recién
   * después el de sección lo desmentía y pausaba, con los bytes ya pedidos. Medido en la ficha del
   * pulidor en escritorio: 4 videos bajando con la fila a 2794px por debajo del fold. Lo siembra a
   * mano `#observeItems` con una medición sincrónica, así que este valor sólo cubre la ventana entre
   * la construcción del objeto y esa siembra.
   */
  #sectionVisible = false;

  /** Tarjetas que ahora mismo intersectan la ventana del carrusel. @type {Set<Element>} */
  #visible = new Set();

  /**
   * Tarjetas que acaban de entrar en pantalla y tienen que arrancar de cero.
   *
   * Sin esto, cada copia acumula su propio tiempo mientras estuvo a la vista, y al deslizar hasta
   * ella la encontrás por la mitad — se lee como si el clon estuviera "linkeado" al original, aunque
   * está medido que no lo está. Reiniciar al entrar es además lo que hacen Instagram y TikTok.
   * @type {Set<Element>}
   */
  #restart = new Set();

  /**
   * Tarjetas que el cliente puso en pausa, por ELEMENTO. Cada copia se maneja sola: pausar una no
   * toca a las demás, aunque muestren el mismo reel. Se probó espejando la pausa por índice de reel y
   * el usuario pidió explícitamente lo contrario — cada tarjeta que ve en pantalla es un control
   * independiente, que es lo que uno espera al tocar una en particular.
   *
   * Hace falta anotarlo en algún lado igual: sin esto el `IntersectionObserver` volvería a arrancar
   * el video en el siguiente cambio de intersección. Va un `WeakSet` para que los clones que borra
   * `#setup` no queden retenidos.
   * @type {WeakSet<Element>}
   */
  #pausedItems = new WeakSet();

  /** @type {number | undefined} */
  #progressFrame;

  /** Último avance pintado por barra, para no reescribir la variable sin necesidad. @type {WeakMap<Element, { ratio: number, pct: number }>} */
  #lastProgress = new WeakMap();

  /** Barra que se está arrastrando ahora mismo. @type {HTMLElement | null} */
  #scrubBar = null;

  /** Hoja de descripción abierta. Sólo puede haber una en toda la fila. @type {HTMLElement | null} */
  #openSheet = null;

  /** Botón "ver más" que abrió esa hoja, para devolverle el foco. @type {HTMLElement | null} */
  #sheetOpener = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setup();

    window.addEventListener('resize', this.#handleResize);
    document.addEventListener('visibilitychange', this.#handleVisibility);

    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointermove', this.#handlePointerMove);
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#handlePointerUp);
    // En captura: hay que llegar antes que los botones de la tarjeta para poder cancelar el clic con
    // el que termina un arrastre.
    this.addEventListener('click', this.#handleClick, true);
    this.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    clearTimeout(this.#settleTimer);
    cancelAnimationFrame(this.#inertiaFrame ?? 0);
    cancelAnimationFrame(this.#progressFrame ?? 0);
    this.#progressFrame = undefined;
    this.#handleResize.cancel();
    this.#observer?.disconnect();
    this.#sectionObserver?.disconnect();
    this.#posterObserver?.disconnect();
    this.#postersNear.clear();

    window.removeEventListener('resize', this.#handleResize);
    document.removeEventListener('visibilitychange', this.#handleVisibility);

    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('pointerup', this.#handlePointerUp);
    this.removeEventListener('pointercancel', this.#handlePointerUp);
    this.removeEventListener('click', this.#handleClick, true);
    this.removeEventListener('keydown', this.#handleKeydown);
  }

  /**
   * La API de Section Rendering hace morph de secciones enteras, y el HTML que manda el servidor
   * trae sólo las tarjetas reales — o sea que borraría los clones y dejaría la pista corta. Rehacer
   * el armado acá lo cura solo. El morph saltea los subárboles que no cambiaron, así que esto no se
   * dispara de gusto.
   */
  updatedCallback() {
    super.updatedCallback?.();
    this.#setup();
  }

  /**
   * Mide, clona y arranca. Es idempotente a propósito: `connectedCallback` puede correr más de una
   * vez (mover el nodo, re-render del editor) y un morph puede llamarlo desde `updatedCallback`.
   */
  #setup() {
    const { track, viewport } = this.refs;
    if (!track || !viewport) return;

    // La hoja abierta apunta a un nodo que un morph puede haber reemplazado, y los clones se van a
    // rehacer: la referencia se suelta antes de tocar nada.
    this.#openSheet = null;
    this.#sheetOpener = null;

    // Se desactiva PRIMERO y se reactiva sólo al final, con todo listo. Cualquier salida temprana de
    // las de más abajo tiene que dejar una fila estática común y corriente, no un estado a medias:
    // `data-pr-active` es lo que enciende el CSS de la pista y las flechas, y dejarlo puesto sin
    // clones ni transform deja las tarjetas descolocadas.
    this.#active = false;
    this.removeAttribute('data-pr-active');
    this.#position = 0;
    this.#lead = 0;
    this.#baseOffset = 0;
    track.style.removeProperty('transform');
    track.style.removeProperty('transition-duration');
    this.style.removeProperty('--pr-lead');

    // Los clones se sacan SIEMPRE antes de medir: si no, la segunda pasada mediría la pista ya llena
    // y multiplicaría las copias en cada llamada.
    for (const clone of track.querySelectorAll('[data-pr-clone]')) clone.remove();

    // Acá, y no antes: con los clones ya afuera lo que queda en la pista son las tarjetas reales, que
    // son las únicas que tiene sentido barajar. Y antes de medir, para que el conteo, el ancho de
    // tarjeta y la cantidad de sets clonados salgan de la fila definitiva.
    if (!this.#shuffled && this.hasAttribute('data-shuffle')) {
      this.#shuffleAndPrune(track);
      this.#shuffled = true;
    }

    const items = /** @type {HTMLElement[]} */ ([...track.children]);
    this.#realCount = items.length;

    const viewportWidth = viewport.clientWidth;

    if (this.#realCount === 0 || viewportWidth === 0) {
      this.#observeItems();
      return;
    }

    this.#measuredWidth = window.innerWidth;

    const { gap, cardWidth, lead } = this.#measure(viewportWidth);
    this.#gap = gap;
    this.#cardWidth = cardWidth;

    // El ancho de tarjeta lo calcula el JS y el CSS sólo lo consume, para que el paso de la
    // animación y el ancho real no puedan desincronizarse nunca: son el mismo número.
    this.style.setProperty('--pr-card-width', `${cardWidth}px`);
    this.style.setProperty('--pr-gap', `${gap}px`);

    // Cada tarjeta ocupa `cardWidth + gap` en el patrón que se repite, incluida la última antes del
    // empalme — por eso el gap cuenta una vez por tarjeta y no `columnas - 1` veces.
    this.#setWidth = this.#realCount * (cardWidth + gap);

    // El loop se arma SIEMPRE que haya al menos dos reels, aunque el set entero entre en la ventana.
    // Es una decisión del usuario, tomada con el costo a la vista: cuando el set mide menos que la
    // ventana, las copias conviven en pantalla y el cliente ve el mismo video dos o tres veces a la
    // vez. Desaparece solo en cuanto hay suficientes reels para llenar el ancho.
    //
    // Con UNO solo no hay loop posible: repetir el mismo video a lo ancho de la pantalla no se lee
    // como carrusel sino como error de render. Ahí queda la fila estática y centrada.
    if (this.#realCount < 2) {
      // El corrimiento de centrado se aplica SÓLO con el loop armado: es el espacio donde asoma la
      // tarjeta anterior, y sin loop no hay tarjeta anterior. Dejarlo puesto acá corre hacia la
      // derecha la única tarjeta, que la fila estática ya centra sola — medido en celular: márgenes
      // de 88px y 30px en vez de 59 y 59.
      this.#lead = 0;
      this.#observeItems();
      this.#measureTruncation();
      return;
    }

    this.#lead = lead;
    if (lead > 0) this.style.setProperty('--pr-lead', `${lead}px`);

    // Un set entero ANTES del primero real, y sólo cuando hay asomo izquierdo que llenar.
    //
    // Sin esto la franja `[0, lead)` no tiene ningún hijo: la pista empieza corrida por el relleno de
    // centrado y las copias sólo se agregaban al final, así que a la izquierda no había nada que
    // dibujar. Se veía como "los reels de la izquierda no cargan hasta que los arrastro" — y no era
    // sólo al arrancar: el encaje cae en múltiplos del paso, y todo múltiplo de `setWidth` equivale a
    // la posición 0, así que la fila alternaba entre asomo lleno y asomo vacío.
    //
    // Va un set ENTERO y no las tarjetas justas: un corrimiento que no sea múltiplo de `setWidth`
    // desalinea el patrón que se repite y rompe `#normalize`, que es todo el mecanismo del loop.
    // En escritorio `lead` es 0 y no se antepone nada — serían seis elementos `video` de más.
    const leadingSets = lead > 0 ? 1 : 0;
    this.#baseOffset = leadingSets * this.#setWidth;

    // Con la posición normalizada en `[0, setWidth)`, la ventana nunca necesita mirar más allá de
    // `setWidth + viewportWidth`. El asomo de la izquierda corre todo hacia adentro, así que entra en
    // la cuenta. Se suma un set extra de colchón contra el redondeo subpíxel; la fórmula ya cubre el
    // caso de un set más chico que la ventana, donde `ceil` pide las copias que hagan falta.
    const trailingSets = 1 + Math.ceil((viewportWidth + lead) / this.#setWidth);

    const primera = items[0];
    for (let set = 0; set < leadingSets; set++) {
      for (const item of items) {
        track.insertBefore(this.#cloneItem(item), primera);
      }
    }
    for (let set = 0; set < trailingSets; set++) {
      for (const item of items) {
        track.appendChild(this.#cloneItem(item));
      }
    }

    this.#active = true;
    this.setAttribute('data-pr-active', '');
    this.#apply(false);
    this.#observeItems();
    this.#measureTruncation();
  }

  /**
   * Baraja la fila y se queda con unos pocos videos por producto. Sólo corre en el modo automático
   * (`data-shuffle`), donde el servidor manda TODOS los reels de cada producto y es acá donde se
   * decide cuáles entran: la aleatoriedad tiene que ser del cliente porque la caché de página de
   * Shopify congelaría cualquier semilla de servidor, y entonces "otro video al recargar" no pasaría
   * nunca.
   *
   * El reparto es por RONDAS y no "los primeros N": primero un video de cada producto, después un
   * segundo de los que todavía tengan, y así. Eso evita de una sola vez las dos formas en que la fila
   * se ve mal — que un producto con varios reels acapare el principio, y que dos tarjetas del mismo
   * producto queden pegadas.
   *
   * @param {HTMLElement} track
   */
  #shuffleAndPrune(track) {
    const items = /** @type {HTMLElement[]} */ ([...track.children]);
    if (items.length < 2) return;

    // Fisher-Yates. `Math.random` alcanza de sobra: acá "al azar" quiere decir que el orden no se
    // repita entre cargas, no una garantía criptográfica.
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }

    // El orden de inserción del Map es el orden ya barajado, así que las rondas también salen
    // mezcladas y no siempre arranca el mismo producto.
    /** @type {Map<string, HTMLElement[]>} */
    const porGrupo = new Map();
    for (const item of items) {
      const grupo = item.getAttribute('data-reel-group') ?? '';
      const cola = porGrupo.get(grupo);
      if (cola) cola.push(item);
      else porGrupo.set(grupo, [item]);
    }

    // Los «fijos» (tutoriales, `data-reel-pin`) pasan al frente de su cola: entran siempre dentro de
    // los que se muestran, y el resto se sigue eligiendo al azar para los lugares que quedan.
    for (const [grupo, cola] of porGrupo) {
      porGrupo.set(grupo, [...cola.filter((i) => i.hasAttribute('data-reel-pin')), ...cola.filter((i) => !i.hasAttribute('data-reel-pin'))]);
    }

    const maximo = Math.max(1, Number(this.dataset.perGroup) || 1);
    /** @type {HTMLElement[]} */
    const orden = [];
    for (let ronda = 0; ronda < maximo; ronda++) {
      for (const cola of porGrupo.values()) {
        const item = cola.shift();
        if (item) orden.push(item);
      }
    }

    // Lo que sobra sale del DOM ANTES de clonar y de observar: son tarjetas que esta carga no va a
    // mostrar, y cada una cuesta un `<video>` y una portada. Con el loop clonando hasta cuatro juegos
    // completos, dejarlas puestas multiplicaría el desperdicio.
    for (const cola of porGrupo.values()) {
      for (const item of cola) item.remove();
    }

    // Con un solo grupo (la ficha) el fijo saldría siempre primero, porque encabeza su cola: se
    // baraja lo elegido para que sólo se garantice que esté, no dónde.
    if (porGrupo.size === 1) {
      for (let i = orden.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [orden[i], orden[j]] = [orden[j], orden[i]];
      }
    }

    const fragmento = document.createDocumentFragment();
    for (const item of orden) fragmento.appendChild(item);
    track.appendChild(fragmento);
  }

  /**
   * Geometría de la fila. Sale de la MEDICIÓN real del elemento, nunca de una media query de
   * viewport: abrir el cajón del carrito no cambia el viewport pero sí comprime la página entera, y
   * una fila calculada contra el ancho de la ventana quedaría desbordada.
   *
   * El tope que manda es el ALTO. Una tarjeta 9:16 al ancho de un teléfono mide más que la pantalla,
   * y en escritorio pedir tres columnas daría tarjetas de 700px de alto. Se elige el ancho máximo a
   * partir del alto disponible y recién después se cuentan las columnas.
   *
   * @param {number} width - Ancho medido de la ventana del carrusel.
   * @returns {{ gap: number, cardWidth: number, lead: number }}
   */
  #measure(width) {
    const mobile = width < 750;
    const gap = mobile ? 10 : 16;
    const maxHeight = window.innerHeight * (mobile ? MAX_HEIGHT_RATIO_MOBILE : MAX_HEIGHT_RATIO_DESKTOP);
    const maxWidth = maxHeight * CARD_ASPECT;

    let cardWidth;

    if (mobile) {
      // Una sola tarjeta, centrada, con el mismo asomo de los dos lados: es lo que le dice al cliente
      // que hay más videos sin tener que mostrarle medio segundo de otro.
      cardWidth = Math.max(width * MOBILE_MIN_WIDTH_RATIO, Math.min(width * MOBILE_MAX_WIDTH_RATIO, maxWidth));
    } else {
      const requested = Math.max(1, Number(this.dataset.columns) || 5);
      const target = Math.min(TARGET_CARD_WIDTH, maxWidth);
      const columns = Math.max(MIN_COLUMNS, Math.min(requested + 0.35, width / (target + gap)));
      // El ajuste del merchant es un máximo, no una orden: si con esa cantidad la tarjeta quedaría
      // más alta que la pantalla, el tope de alto gana y se muestran más y más chicas.
      cardWidth = Math.min((width - columns * gap) / columns, maxWidth);
    }

    const columns = width / (cardWidth + gap);
    // El asomo de la izquierda sólo tiene sentido cuando hay una sola tarjeta a la vista. En una fila
    // de cuatro, correr todo hacia adentro dejaría un hueco enorme contra el borde.
    const lead = columns < 2 ? Math.max(0, (width - cardWidth) / 2) : 0;

    return { gap, cardWidth, lead };
  }

  /**
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #cloneItem(item) {
    const clone = /** @type {HTMLElement} */ (item.cloneNode(true));
    clone.setAttribute('data-pr-clone', '');
    clone.setAttribute('aria-hidden', 'true');

    if (clone.hasAttribute('id')) clone.removeAttribute('id');
    for (const el of clone.querySelectorAll('[id]')) el.removeAttribute('id');

    // Los clones se ven y se pueden tocar (en un loop, buena parte de lo que hay en pantalla son
    // clones), pero quedan fuera del recorrido del tabulador: con `aria-hidden` puesto, dejar algo
    // enfocable adentro es un error de accesibilidad. El teclado recorre sólo las reales.
    for (const el of clone.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')) {
      el.setAttribute('tabindex', '-1');
    }

    // El video del clon arranca de cero: si se copiara ya cargado, serían dos decodificadores
    // abiertos sobre el mismo archivo antes de que el observador decida si hace falta.
    for (const video of clone.querySelectorAll('video')) {
      delete video.dataset.srcReady;
      delete video.dataset.playing;
      for (const source of video.querySelectorAll('source')) source.removeAttribute('src');

      // El silencio se fuerza a mano y no se confía en la copia: `cloneNode` replica el ATRIBUTO
      // `muted`, no la propiedad. Hoy el atributo está en el markup, pero si algún día se fuera, cada
      // copia nacería con sonido y el navegador le negaría el autoplay — o sea que el reel se vería
      // congelado, sin ninguna pista de por qué.
      video.muted = true;
    }

    // Estado visual heredado de la tarjeta original. Se vuelve a derivar enseguida, pero copiado tal
    // cual dejaría el relleno de la barra en el avance del otro video hasta el próximo fotograma.
    clone.removeAttribute('data-paused');
    for (const bar of clone.querySelectorAll('[data-reel-progress]')) {
      /** @type {HTMLElement} */ (bar).style.removeProperty('--pr-progress');
      bar.removeAttribute('data-scrubbing');
    }
    for (const sheet of clone.querySelectorAll('[data-reel-sheet]')) sheet.setAttribute('hidden', '');

    return clone;
  }

  /* ------------------------------------------------------------------ movimiento de la pista */

  /**
   * Avanza `count` tarjetas con transición. Con `count` negativo, retrocede.
   *
   * Regla de oro del loop, y el bug más caro de esta familia: **una posición negativa es pista que
   * no existe**. El `transform` se vuelve positivo, la pista se corre a la derecha y durante toda la
   * transición queda un hueco en blanco pegado al borde izquierdo. `product-carousel.js` sólo avanza
   * y por eso le alcanzaba con normalizar al final; acá las flechas también retroceden, así que hay
   * que asegurar el rango ANTES de animar. Medido con las flechas de este mismo componente: sin este
   * ajuste, el primer clic en "anterior" desde el arranque dejaba el transform en +256px.
   *
   * El acomodo previo va con la transición APAGADA, que es lo que lo hace invisible: el contenido en
   * `p` y en `p ± setWidth` es idéntico píxel a píxel. Y de paso pone techo a una ráfaga de clics —
   * sin re-normalizar en cada paso, la posición se acumularía más allá del último set clonado y la
   * pista se terminaría de verdad.
   *
   * @param {number} count
   */
  #step(count) {
    if (!this.#active) return;

    const delta = count * (this.#cardWidth + this.#gap);
    const before = this.#position;

    this.#normalize();
    if (this.#position + delta < 0) this.#position += this.#setWidth;

    if (this.#position !== before) {
      this.#apply(false);
      // Reflow forzado. Sin esto el navegador junta el salto y la animación en un solo cambio de
      // estilo — el acomodo deja de ser invisible y se ve el brinco de un set entero.
      void this.refs.track?.offsetWidth;
    }

    this.#position += delta;
    this.#apply(true);
    this.#scheduleSettle();
  }

  /**
   * Al terminar el deslizamiento devuelve la posición al rango `[0, setWidth)` sin transición. Es
   * invisible porque el contenido en `p` y en `p - setWidth` es idéntico píxel a píxel — de eso se
   * trata todo el "infinito".
   */
  #scheduleSettle() {
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => {
      if (this.#dragging) return;
      const before = this.#position;
      this.#normalize();
      if (this.#position !== before) this.#apply(false);
    }, STEP_MS + 30);
  }

  /** @param {boolean} animated */
  #apply(animated) {
    const { track } = this.refs;
    if (!track) return;

    track.style.transitionDuration = animated && !prefersReducedMotion() ? `${STEP_MS}ms` : '0ms';
    // `#baseOffset` es una constante de dibujo (los sets clonados que hay adelante), no estado: se
    // suma acá y en ningún otro lado, para que `#position`, `#step`, la inercia y `#normalize` sigan
    // razonando sobre el mismo rango `[0, setWidth)` de siempre.
    track.style.transform = `translate3d(${-(this.#position + this.#baseOffset)}px, 0, 0)`;
  }

  /** Normaliza la posición a `[0, setWidth)`. Es lo único que hace falta para que no haya topes. */
  #normalize() {
    if (this.#setWidth <= 0) return;
    this.#position = ((this.#position % this.#setWidth) + this.#setWidth) % this.#setWidth;
  }

  #handleResize = debounce(() => {
    // Un resize sólo vertical es, en el celular, la barra de direcciones apareciendo y
    // desapareciendo al scrollear. Rehacer el armado ahí tira el carrusel al piso sin motivo —
    // mismo criterio que `marquee.js` y `product-carousel.js`.
    if (window.innerWidth === this.#measuredWidth && this.#active) return;
    this.#setup();
  }, 250);

  /* ------------------------------------------------------------------------------- arrastre */

  #handlePointerDown = (/** @type {PointerEvent} */ event) => {
    if (event.button !== 0) return;

    const target = event.target instanceof Element ? event.target : null;

    // La barra de progreso se queda con el gesto entero: el carrusel no tiene que arrastrarse
    // mientras el cliente está adelantando el video. Va ANTES del control de `#active` porque
    // adelantar tiene que funcionar también con un solo reel, donde no hay loop.
    const bar = target?.closest('[data-reel-progress]');
    if (bar instanceof HTMLElement) {
      this.#beginScrub(bar, event);
      return;
    }

    if (!this.#active) return;

    cancelAnimationFrame(this.#inertiaFrame ?? 0);
    clearTimeout(this.#settleTimer);

    this.#dragging = true;
    this.#dragMoved = 0;
    this.#dragStartX = event.clientX;
    this.#dragStartPosition = this.#position;
    this.#velocity = 0;
    this.#samples = [{ x: event.clientX, t: event.timeStamp }];

    // NO se captura el puntero acá, y esa es la clave de que se pueda tocar una tarjeta.
    // `setPointerCapture` redirige toda la secuencia del puntero al elemento que captura, y el
    // `click` que sale de esa secuencia se dispara sobre el capturador — nunca llega al botón de la
    // tarjeta. Capturando en el `pointerdown` quedaba un carrusel que se arrastraba pero en el que
    // NINGÚN control se podía tocar. La captura se pide recién cuando el gesto supera el umbral y ya
    // es inequívocamente un arrastre (ver `#handlePointerMove`).
    this.#captured = false;
  };

  /** Convierte el gesto en arrastre: recién acá se captura el puntero y se apagan los clics. */
  #beginDrag(/** @type {PointerEvent} */ event) {
    this.#captured = true;
    this.setAttribute('dragging', '');
    // Leer con una hoja abierta y que la fila se mueva debajo es un estado incoherente: el arrastre
    // la cierra.
    this.#closeSheet();
    // Lanza `NotFoundError` si el puntero ya no está activo (se soltó fuera de la ventana, o el
    // evento es sintético). No es motivo para abortar: sin captura el arrastre igual funciona
    // mientras el cursor no se vaya del elemento.
    try {
      this.setPointerCapture(event.pointerId);
    } catch {
      /* sin captura, el arrastre igual funciona */
    }
  }

  #handlePointerMove = (/** @type {PointerEvent} */ event) => {
    if (this.#scrubBar) {
      this.#seekFromPointer(this.#scrubBar, event.clientX);
      return;
    }

    if (!this.#dragging) return;

    const delta = event.clientX - this.#dragStartX;
    this.#dragMoved = Math.max(this.#dragMoved, Math.abs(delta));

    if (!this.#captured) {
      // Por debajo del umbral todavía puede ser un toque: no se toca nada, para no robarle el
      // evento a los controles de la tarjeta.
      if (this.#dragMoved <= DRAG_THRESHOLD) return;
      this.#beginDrag(event);
    }

    this.#position = this.#dragStartPosition - delta;
    this.#normalize();
    this.#apply(false);

    this.#samples.push({ x: event.clientX, t: event.timeStamp });
    if (this.#samples.length > 5) this.#samples.shift();
  };

  #handlePointerUp = (/** @type {PointerEvent} */ event) => {
    if (this.#scrubBar) {
      this.#endScrub(event);
      return;
    }

    if (!this.#dragging) return;

    this.#dragging = false;
    this.removeAttribute('dragging');
    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);

    // Nunca llegó a ser un arrastre: fue un toque. No hay que encajar la pista ni lanzar inercia —
    // un reacomodo acá movería la tarjeta justo cuando el cliente la está tocando.
    if (!this.#captured) {
      this.#samples = [];
      return;
    }

    // Velocidad de las últimas muestras, no de todo el gesto: lo que importa es cómo venía la mano
    // al soltar, no el promedio del arrastre entero.
    const first = this.#samples[0];
    const last = this.#samples[this.#samples.length - 1];
    const elapsed = last && first ? last.t - first.t : 0;
    this.#velocity = elapsed > 0 ? (-(last.x - first.x) / elapsed) * 16 : 0;

    this.#samples = [];
    this.#runInertia();
  };

  /** Desliza con la velocidad de salida, frena por fricción y encaja en el múltiplo más cercano. */
  #runInertia() {
    const stepDistance = this.#cardWidth + this.#gap;
    if (stepDistance <= 0) return;

    const frame = () => {
      if (this.#dragging) return;

      this.#velocity *= FRICTION;

      if (Math.abs(this.#velocity) < MIN_VELOCITY) {
        // El encaje final es lo único de la inercia que va animado, y por eso acá NO se normaliza:
        // durante el arrastre la posición se mantiene en `[0, setWidth)` fotograma a fotograma, así
        // que un encaje cerca del extremo superior se iría a `setWidth` y normalizar lo devolvería a
        // 0 — con la transición encendida, eso es un rebobinado de un set entero a la vista. Se
        // encaja donde está y el módulo lo arregla después, con la transición apagada.
        this.#position = Math.round(this.#position / stepDistance) * stepDistance;
        this.#apply(true);
        this.#scheduleSettle();
        return;
      }

      this.#position += this.#velocity;
      this.#normalize();
      this.#apply(false);
      this.#inertiaFrame = requestAnimationFrame(frame);
    };

    this.#inertiaFrame = requestAnimationFrame(frame);
  }

  /* ------------------------------------------------------------------------ clics y teclado */

  /**
   * Un solo enrutador para todos los clics del componente, en fase de captura.
   *
   * Va todo por acá y no por los atributos de evento declarativos del tema porque `component.js` los
   * delega a nivel documento, también en captura: correrían ANTES que esta cancelación y arrastrar
   * la fila terminaría pausando el video que quedó bajo el dedo.
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    // El arrastre termina en un `click` sobre lo que quedó bajo el dedo. El umbral distingue el
    // arrastre real del temblor de la mano al hacer un toque normal, que sí tiene que pasar.
    if (this.#dragMoved > DRAG_THRESHOLD) {
      event.preventDefault();
      event.stopPropagation();
      this.#dragMoved = 0;
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // La barra ya hizo todo su trabajo en el `pointerdown`: acá sólo hay que no dejar que el clic
    // siga viaje hasta el play/pausa que tiene debajo.
    if (target.closest('[data-reel-progress]')) return;

    const arrow = target.closest('[data-reel-arrow]');
    if (arrow instanceof HTMLElement) {
      this.#closeSheet();
      this.#step(arrow.dataset.reelArrow === 'prev' ? -1 : 1);
      return;
    }

    const item = target.closest('.product-reels__item');

    if (item instanceof HTMLElement && target.closest('[data-reel-more]')) {
      this.#openSheetFor(item);
      return;
    }

    const sound = target.closest('[data-reel-sound]');
    if (sound instanceof HTMLElement) {
      this.#toggleCardSound(sound);
      return;
    }

    // Con una descripción abierta, cualquier toque que no sea un control la cierra: la hoja misma, su
    // botón de cerrar, y también el video que queda a la vista arriba del panel. Ese último es el que
    // importa — sin esta regla el mismo gesto significaría dos cosas según dónde caiga el dedo
    // (descartar si toca el panel, pausar si toca dos centímetros más arriba).
    if (this.#openSheet) {
      this.#closeSheet();
      return;
    }

    if (item instanceof HTMLElement && target.closest('[data-reel-toggle]')) {
      this.#toggleCardPlayback(item);
    }
  };

  #handleKeydown = (/** @type {KeyboardEvent} */ event) => {
    const target = event.target instanceof Element ? event.target : null;
    const bar = target?.closest('[data-reel-progress]');

    if (bar instanceof HTMLElement) {
      const item = bar.closest('.product-reels__item');
      const video = item?.querySelector('video');
      if (!(item instanceof HTMLElement) || !(video instanceof HTMLVideoElement)) return;
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;

      let time;
      if (event.key === 'ArrowRight') time = video.currentTime + SEEK_STEP;
      else if (event.key === 'ArrowLeft') time = video.currentTime - SEEK_STEP;
      else if (event.key === 'Home') time = 0;
      else if (event.key === 'End') time = video.duration;
      else return;

      event.preventDefault();
      this.#seekRatio(item, video, time / video.duration);
      return;
    }

    if (event.key === 'Escape' && this.#openSheet) {
      event.preventDefault();
      this.#closeSheet();
    }
  };

  /* ------------------------------------------------------------------------- barra de progreso */

  /**
   * @param {HTMLElement} bar
   * @param {PointerEvent} event
   */
  #beginScrub(bar, event) {
    this.#scrubBar = bar;
    bar.setAttribute('data-scrubbing', '');
    // Acá la captura SÍ es segura, al revés que en el arrastre de la fila: la barra no tiene nada
    // adentro que necesite recibir el clic, así que redirigirle la secuencia no rompe nada.
    try {
      bar.setPointerCapture(event.pointerId);
    } catch {
      /* sin captura, adelantar igual funciona mientras el dedo no se vaya de la barra */
    }
    this.#seekFromPointer(bar, event.clientX);
  }

  /** @param {PointerEvent} event */
  #endScrub(event) {
    const bar = this.#scrubBar;
    this.#scrubBar = null;
    if (!bar) return;

    bar.removeAttribute('data-scrubbing');
    if (bar.hasPointerCapture(event.pointerId)) bar.releasePointerCapture(event.pointerId);
  }

  /**
   * @param {HTMLElement} bar
   * @param {number} clientX
   */
  #seekFromPointer(bar, clientX) {
    const item = bar.closest('.product-reels__item');
    const video = item?.querySelector('video');
    if (!(item instanceof HTMLElement) || !(video instanceof HTMLVideoElement)) return;

    const rect = bar.getBoundingClientRect();
    if (rect.width <= 0) return;

    this.#seekRatio(item, video, (clientX - rect.left) / rect.width);
  }

  /**
   * Adelanta a una fracción del video. Si el archivo todavía no se descargó no hay duración contra
   * la cual calcular: se promueve la fuente y el salto queda pendiente de los metadatos, para que la
   * barra nunca se sienta muerta.
   *
   * @param {HTMLElement} item
   * @param {HTMLVideoElement} video
   * @param {number} ratio
   */
  #seekRatio(item, video, ratio) {
    const clamped = Math.min(1, Math.max(0, ratio));

    const aplicar = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      // El último instante exacto puede disparar el final del video en vez de mostrar el cuadro.
      video.currentTime = Math.min(video.duration - 0.05, clamped * video.duration);
      this.#paintProgress(item, video);
    };

    if (Number.isFinite(video.duration) && video.duration > 0) {
      aplicar();
      return;
    }

    this.#ensureSource(video);
    video.addEventListener('loadedmetadata', aplicar, { once: true });
  }

  /**
   * Bucle de avance de las barras visibles.
   *
   * Va por `requestAnimationFrame` y no por el evento `timeupdate` del video: ese evento llega unas
   * cuatro veces por segundo, así que la barra avanzaría a saltos visibles. Se apaga solo en cuanto
   * no queda nada reproduciéndose o la pestaña deja de estar a la vista, y lo vuelve a encender
   * `#syncPlayback`.
   */
  #tickProgress = () => {
    this.#progressFrame = undefined;

    let alguno = false;

    for (const item of this.#visible) {
      const video = item.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) continue;
      if (!video.paused) alguno = true;
      this.#paintProgress(/** @type {HTMLElement} */ (item), video);
    }

    if (alguno && !document.hidden) this.#ensureProgressLoop();
  };

  #ensureProgressLoop() {
    if (this.#progressFrame !== undefined) return;
    this.#progressFrame = requestAnimationFrame(this.#tickProgress);
  }

  /**
   * @param {HTMLElement} item
   * @param {HTMLVideoElement} video
   */
  #paintProgress(item, video) {
    const bar = item.querySelector('[data-reel-progress]');
    if (!(bar instanceof HTMLElement)) return;

    const duration = video.duration;
    const ratio =
      Number.isFinite(duration) && duration > 0 ? Math.min(1, Math.max(0, video.currentTime / duration)) : 0;

    const previo = this.#lastProgress.get(bar);
    if (previo && Math.abs(ratio - previo.ratio) < PROGRESS_EPSILON) return;

    bar.style.setProperty('--pr-progress', String(ratio));

    // El texto para lectores de pantalla se rehace sólo al cambiar de punto porcentual: reescribir
    // dos atributos sesenta veces por segundo, en seis barras, no le sirve a nadie.
    const pct = Math.round(ratio * 100);
    if (!previo || previo.pct !== pct) {
      bar.setAttribute('aria-valuenow', String(pct));
      bar.setAttribute('aria-valuetext', `${formatTime(video.currentTime)} de ${formatTime(duration)}`);
    }

    this.#lastProgress.set(bar, { ratio, pct });
  }

  /* ------------------------------------------------------------------------ sonido y descripción */

  /**
   * Enciende o apaga el sonido de UNA tarjeta de la fila.
   *
   * Suena una sola a la vez, y a propósito: en el loop las copias de un mismo reel conviven en
   * pantalla, así que encender el sonido de todas las que muestran ese video daría el mismo audio
   * superpuesto y desfasado. Es lo contrario a la pausa, que sí se espeja — el audio es un canal
   * único y la pausa es una propiedad del contenido.
   *
   * @param {HTMLElement} button
   */
  #toggleCardSound(button) {
    const item = button.closest('.product-reels__item');
    const video = item?.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    const encender = video.muted;
    if (encender) this.#muteRow();

    video.muted = !encender;
    button.setAttribute('aria-pressed', String(!encender));
    button.setAttribute('aria-label', encender ? 'Silenciar' : 'Activar sonido');
  }

  /** Devuelve la fila entera al silencio y deja los botones diciendo la verdad. */
  #muteRow() {
    const { track } = this.refs;
    if (!track) return;

    for (const video of track.querySelectorAll('video')) video.muted = true;
    for (const button of track.querySelectorAll('[data-reel-sound]')) {
      button.setAttribute('aria-pressed', 'true');
      button.setAttribute('aria-label', 'Activar sonido');
    }
  }

  /**
   * Mide qué descripciones quedan cortadas para decidir dónde va el "ver más". Un botón que abre
   * exactamente el texto que ya se está leyendo es ruido.
   *
   * Se espera a que las fuentes estén listas: medir con la tipografía de respaldo da un ancho que no
   * es el final, y el texto se remaqueta al llegar Archivo e Inter — el botón quedaría puesto o
   * sacado por error en la mitad de las tarjetas.
   */
  #measureTruncation() {
    const { track } = this.refs;
    if (!track) return;

    const medir = () => {
      for (const item of track.querySelectorAll('.product-reels__item')) {
        const desc = item.querySelector('.product-reels__desc');
        if (!(desc instanceof HTMLElement)) continue;
        item.toggleAttribute('data-truncated', desc.scrollWidth > desc.clientWidth + 1);
      }
    };

    const fuentes = document.fonts?.ready ?? Promise.resolve();
    fuentes.then(() => requestAnimationFrame(medir));
  }

  /** @param {HTMLElement} item */
  #openSheetFor(item) {
    const sheet = item.querySelector('[data-reel-sheet]');
    if (!(sheet instanceof HTMLElement)) return;

    // Una sola abierta en toda la fila: dos capas de texto sobre dos videos distintos no se leen.
    this.#closeSheet();

    sheet.hidden = false;
    item.setAttribute('data-sheet-open', '');
    // También en el componente: las flechas se apartan mientras se lee. No es sólo prolijidad —
    // la pista declara un `transform`, o sea que es un contexto de apilamiento, y todo lo que vive
    // adentro (la hoja incluida) queda encerrado ahí por más z-index que se le ponga. La flecha, que
    // es hermana de la ventana, le gana siempre. Medido: la flecha izquierda tapaba el texto de la
    // descripción. Subir el z-index de la hoja no puede arreglarlo; apartar la flecha, sí.
    this.setAttribute('data-sheet-open', '');
    this.#openSheet = sheet;
    this.#sheetOpener = /** @type {HTMLElement | null} */ (item.querySelector('[data-reel-more]'));

    // El foco entra en la hoja para que Escape la cierre y para que quien navega con teclado no
    // quede leyendo detrás de una capa. `preventScroll` evita el salto de página en celular.
    /** @type {HTMLElement | null} */ (sheet.querySelector('[data-reel-sheet-close]'))?.focus({
      preventScroll: true,
    });
  }

  #closeSheet() {
    const sheet = this.#openSheet;
    if (!sheet) return;

    sheet.hidden = true;
    sheet.closest('.product-reels__item')?.removeAttribute('data-sheet-open');
    this.removeAttribute('data-sheet-open');
    this.#openSheet = null;

    const opener = this.#sheetOpener;
    this.#sheetOpener = null;

    // El foco vuelve SÓLO si estaba adentro de la hoja. Si el cierre vino de un arrastre o de otro
    // punto de la página, moverlo sería robárselo a donde el cliente estaba.
    if (opener && sheet.contains(document.activeElement)) opener.focus({ preventScroll: true });
  }

  /* --------------------------------------------------------------- reproducción de la fila */

  /**
   * Observa cada tarjeta contra la ventana del carrusel para reproducir SÓLO lo que está a la vista.
   * Es la pieza que hace viable el loop con video: entre reales y clones puede haber quince
   * elementos de video en el DOM, y quince reproduciéndose a la vez agotan los decodificadores del
   * dispositivo (iOS Safari directamente los tira). Como sólo corre lo que intersecta, el máximo
   * simultáneo queda en las columnas visibles más una.
   *
   * No existe nada parecido en el tema: ningún `IntersectionObserver` de Horizon toca un video.
   */
  #observeItems() {
    this.#observer?.disconnect();
    this.#visible.clear();

    const { viewport, track } = this.refs;
    if (!viewport || !track) return;

    // Segundo observador, contra la PANTALLA (root nulo), sobre la sección entera. El de las tarjetas
    // no puede hacer este trabajo: con una raíz explícita la intersección se mide contra esa raíz y
    // no contra el viewport, así que una sección scrolleada fuera de pantalla sigue teniendo tarjetas
    // "visibles" y sus videos siguen sonando.
    //
    // Va ANTES que el otro y con el estado sembrado a mano, y las dos cosas son el mismo arreglo: los
    // observadores entregan su primera observación en orden de creación, así que creándolo después el
    // de las tarjetas corría con `#sectionVisible` sin desmentir y bajaba los videos de una sección
    // que estaba a 2794px del fold. Sembrarlo cubre además el caso de que esa primera entrega llegue
    // tarde. Es la carga diferida real: sin esto, "preload=none" y el `src` postergado no servían de
    // nada en la primera pantalla.
    this.#sectionVisible = this.#isOnScreen();

    if (!this.#sectionObserver) {
      this.#sectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          if (!entry) return;
          this.#sectionVisible = entry.isIntersecting;
          this.#flushPosters();
          this.#syncPlayback();
        },
        { threshold: 0 }
      );
      this.#sectionObserver.observe(this);
    }

    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: viewport,
      threshold: 0.5,
    });

    // Se rehace junto con el de las tarjetas: `#setup` borra y vuelve a crear los clones, y un
    // observador sobre nodos que ya no están en el DOM no avisa nada.
    this.#posterObserver?.disconnect();
    this.#postersNear.clear();
    this.#posterObserver = new IntersectionObserver(this.#handlePosterIntersect, {
      root: viewport,
      rootMargin: '0px 100% 0px 100%',
      threshold: 0,
    });

    for (const item of track.querySelectorAll('.product-reels__item')) {
      this.#observer.observe(item);
      if (item.querySelector('img[data-src]')) this.#posterObserver.observe(item);
    }
  }

  #handlePosterIntersect = (/** @type {IntersectionObserverEntry[]} */ entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) this.#postersNear.add(entry.target);
      else this.#postersNear.delete(entry.target);
    }
    this.#flushPosters();
  };

  /**
   * Promueve los pósters de las tarjetas cercanas, sólo con la sección en pantalla. Es el único
   * lugar que toca `src` de un póster: el observador de arriba anota, éste decide.
   */
  #flushPosters() {
    if (!this.#sectionVisible || this.#postersNear.size === 0) return;

    for (const item of this.#postersNear) {
      this.#revealPoster(item);
      this.#postersNear.delete(item);
      this.#posterObserver?.unobserve(item);
    }
  }

  /**
   * Devuelve al `<img>` lo que el Liquid le guardó en `data-*`. Primero `srcset` y después `src`,
   * para que el navegador elija candidato una sola vez y no pida el de 900 "por si acaso".
   *
   * @param {Element} item
   */
  #revealPoster(item) {
    for (const img of item.querySelectorAll('img[data-src]')) {
      const srcset = img.getAttribute('data-srcset');
      const src = img.getAttribute('data-src');
      const alt = img.getAttribute('data-alt');
      if (srcset) img.setAttribute('srcset', srcset);
      if (src) img.setAttribute('src', src);
      if (alt !== null) img.setAttribute('alt', alt);
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
      img.removeAttribute('data-alt');
    }
  }

  /**
   * Si la sección cae dentro de la pantalla en este instante. Se mide a mano en vez de esperar al
   * observador porque hace falta el dato SINCRÓNICAMENTE, antes de que nada pueda pedir un byte.
   */
  #isOnScreen() {
    const rect = this.getBoundingClientRect();
    const alto = window.innerHeight || document.documentElement.clientHeight || 0;
    return rect.bottom > 0 && rect.top < alto;
  }

  #handleIntersect = (/** @type {IntersectionObserverEntry[]} */ entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        // Sólo en la TRANSICIÓN de fuera a dentro: `#syncPlayback` se llama muchas veces por otros
        // motivos y reiniciar en cada una cortaría el video que se está mirando.
        if (!this.#visible.has(entry.target)) this.#restart.add(entry.target);
        this.#visible.add(entry.target);
      } else {
        this.#visible.delete(entry.target);
      }
    }

    this.#syncPlayback();
  };

  #syncPlayback() {
    const { track } = this.refs;
    if (!track) return;

    // La fila entera se queda quieta si la pestaña está oculta, si el cliente pidió movimiento
    // reducido, o si la SECCIÓN se fue de la pantalla. Esa última es la que evita que el audio siga
    // sonando de fondo mientras se lee el resto de la ficha.
    const silence = document.hidden || prefersReducedMotion() || !this.#sectionVisible;

    for (const item of track.querySelectorAll('.product-reels__item')) {
      const video = item.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) continue;

      const pausado = this.#pausedItems.has(item);

      if (!silence && !pausado && this.#visible.has(item)) {
        // Cada vez que una tarjeta vuelve a entrar en pantalla arranca de cero, como en cualquier
        // visor de reels. Se hace acá y no en el observador para no reiniciar una que el cliente dejó
        // en pausa a propósito: esta rama es la única que además la va a reproducir.
        if (this.#restart.delete(item) && video.currentTime > 0.05) {
          try {
            video.currentTime = 0;
          } catch {
            /* si todavía no hay metadatos, no hay nada que rebobinar */
          }
        }
        this.#playVideo(video);
      } else {
        video.pause();
      }

      this.#syncCardState(/** @type {HTMLElement} */ (item), video);
    }

    this.#ensureProgressLoop();
  }

  /**
   * Enciende o apaga ESTA tarjeta, y sólo ésta: las otras copias del mismo reel siguen como estaban.
   * La decisión se anota y la aplica `#syncPlayback`, que es el único que toca `play`/`pause`.
   *
   * @param {HTMLElement} item
   */
  #toggleCardPlayback(item) {
    const video = item.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    if (this.#pausedItems.has(item)) this.#pausedItems.delete(item);
    else this.#pausedItems.add(item);

    this.#syncPlayback();
  }

  /**
   * Refleja en el DOM el estado real de la tarjeta. El ícono central aparece con una pausa DEL
   * CLIENTE o con un video que todavía no pintó un cuadro; las pausas automáticas por salir de
   * pantalla no lo encienden, o cada tarjeta que sale y vuelve daría un parpadeo del play.
   *
   * @param {HTMLElement} item
   * @param {HTMLVideoElement} video
   */
  #syncCardState(item, video) {
    const detenido = this.#pausedItems.has(item) || !video.dataset.playing;
    item.toggleAttribute('data-paused', detenido);

    const stage = item.querySelector('[data-reel-toggle]');
    if (!stage) return;

    const titulo = item.querySelector('.product-reels__title')?.textContent?.trim();
    const etiqueta = `${detenido ? 'Reproducir' : 'Pausar'} el video${titulo ? `: ${titulo}` : ''}`;
    if (stage.getAttribute('aria-label') !== etiqueta) stage.setAttribute('aria-label', etiqueta);
  }

  /**
   * Promueve la URL diferida a `src`. Viajaba en `data-video-source` justamente para llegar hasta
   * acá: recién con la tarjeta a la vista (o con el cliente tocándola) este video baja un byte.
   *
   * @param {HTMLVideoElement} video
   */
  #ensureSource(video) {
    if (video.dataset.srcReady) return;

    for (const source of video.querySelectorAll('source[data-video-source]')) {
      const url = source.getAttribute('data-video-source');
      if (url) source.setAttribute('src', url);
    }
    video.dataset.srcReady = 'true';
    video.load();

    // La bandera que revela el video sobre la portada la pone el evento `playing`, NUNCA la promesa
    // de `play()`. Está medido en `assets/product-card.js`: con `preload="none"` esa promesa puede
    // resolver antes de que se decodifique un solo cuadro, o no resolver nunca, y el video quedaba
    // invisible aunque estuviera corriendo.
    video.addEventListener(
      'playing',
      () => {
        video.dataset.playing = 'true';
        const item = video.closest('.product-reels__item');
        if (item instanceof HTMLElement) this.#syncCardState(item, video);
      },
      { once: true }
    );
  }

  /** @param {HTMLVideoElement} video */
  #playVideo(video) {
    this.#ensureSource(video);

    video.play().catch(() => {
      /* el navegador puede negar la reproducción; queda la portada con el ícono de play, que es un
         estado válido y honesto */
    });
  }

  #handleVisibility = () => {
    this.#syncPlayback();
  };
}

if (!customElements.get('product-reels-component')) {
  customElements.define('product-reels-component', ProductReels);
}
